/**
 * As cores do kit Tailwind do Logigate.
 *
 * Os nomes são os do Mantine (`teal`, `grape`, `dark`…) porque muitas cores chegam do
 * servidor assim (`estado_cor`, `cor_triagem`) e porque as páginas que passam do Mantine
 * para aqui mantêm o mesmo `color`. `marca` é a cor do portal (`definirCorDoPortal`, no fim
 * deste ficheiro): o verde por omissão, o azul do logótipo no Logigate (`logigate`), azul no morador, índigo no funcionário.
 *
 * As classes estão todas escritas por extenso: o Tailwind só gera as que encontra no
 * código, e uma classe montada com `bg-${cor}-600` nunca chegava ao CSS. O ficheiro é
 * `.tsx` (e não `.ts`) pela mesma razão: o `content` do tailwind.config só lê `*.tsx`.
 */

export type Cor =
    | "marca"
    | "gray"
    | "dark"
    | "red"
    | "pink"
    | "grape"
    | "violet"
    | "indigo"
    | "blue"
    | "cyan"
    | "teal"
    | "green"
    | "lime"
    | "yellow"
    | "orange";

export interface Paleta {
    /** Fundo cheio e texto por cima. */
    cheio: string;
    cheioHover: string;
    /** Fundo claro e texto escuro da mesma cor. */
    suave: string;
    suaveHover: string;
    /** Borda e texto da cor, fundo branco. */
    contorno: string;
    contornoHover: string;
    /** Só o texto, fundo a aparecer ao passar. */
    fantasmaHover: string;
    /** O texto da cor, em cima de branco. */
    texto: string;
    /** Um ponto ou uma barra da cor. */
    ponto: string;
    /** Caixa de aviso: borda, fundo e texto. */
    aviso: string;
    /** O anel de foco. */
    anel: string;
    /** O aro fino dos selos (o `ring-inset` do Tailwind UI). */
    aro: string;
}

const PALETAS: Record<Cor, Paleta> = {
    marca: {
        cheio: "bg-portal text-white dark:bg-portal-claro dark:text-zinc-950",
        cheioHover: "hover:bg-portal-forte dark:hover:brightness-110",
        suave: "bg-portal-suave text-portal-texto dark:bg-portal-claro/10 dark:text-portal-claro",
        suaveHover: "hover:bg-portal/10 dark:hover:bg-portal-claro/20",
        contorno: "border border-portal/40 text-portal-texto dark:border-portal-claro/40 dark:text-portal-claro",
        contornoHover: "hover:bg-portal-suave dark:hover:bg-portal-claro/10",
        fantasmaHover: "text-portal-texto hover:bg-portal-suave dark:text-portal-claro dark:hover:bg-portal-claro/10",
        texto: "text-portal dark:text-portal-claro",
        ponto: "bg-portal",
        aviso: "border-portal/20 bg-portal-suave/70 text-portal-texto dark:border-portal-claro/20 dark:bg-portal-claro/5 dark:text-portal-claro",
        anel: "focus-visible:ring-portal/40",
        aro: "ring-portal/20 dark:ring-portal-claro/30",
    },
    gray: {
        cheio: "bg-zinc-500 text-white",
        cheioHover: "hover:bg-zinc-600",
        suave: "bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300",
        suaveHover: "hover:bg-zinc-200 dark:hover:bg-zinc-700",
        contorno: "border border-zinc-300 text-zinc-700 dark:border-zinc-700 dark:text-zinc-300",
        contornoHover: "hover:bg-zinc-50 dark:hover:bg-zinc-800",
        fantasmaHover: "text-zinc-700 hover:bg-zinc-100 dark:text-zinc-300 dark:hover:bg-zinc-800",
        texto: "text-zinc-600 dark:text-zinc-400",
        ponto: "bg-zinc-400",
        aviso: "border-zinc-200 bg-zinc-50 text-zinc-800 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-200",
        anel: "focus-visible:ring-zinc-500/40",
        aro: "ring-zinc-500/20 dark:ring-zinc-400/20",
    },
    dark: {
        cheio: "bg-zinc-800 text-white dark:bg-zinc-200 dark:text-zinc-900",
        cheioHover: "hover:bg-zinc-900 dark:hover:bg-white",
        suave: "bg-zinc-200 text-zinc-900 dark:bg-zinc-700 dark:text-zinc-100",
        suaveHover: "hover:bg-zinc-300 dark:hover:bg-zinc-600",
        contorno: "border border-zinc-500 text-zinc-900 dark:border-zinc-500 dark:text-zinc-100",
        contornoHover: "hover:bg-zinc-100 dark:hover:bg-zinc-800",
        fantasmaHover: "text-zinc-900 hover:bg-zinc-100 dark:text-zinc-100 dark:hover:bg-zinc-800",
        texto: "text-zinc-900 dark:text-zinc-100",
        ponto: "bg-zinc-800",
        aviso: "border-zinc-300 bg-zinc-100 text-zinc-900 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100",
        anel: "focus-visible:ring-zinc-500/40",
        aro: "ring-zinc-700/20 dark:ring-zinc-300/20",
    },
    red: {
        cheio: "bg-red-600 text-white",
        cheioHover: "hover:bg-red-700 active:bg-red-800",
        suave: "bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-300",
        suaveHover: "hover:bg-red-100 dark:hover:bg-red-500/20",
        contorno: "border border-red-300 text-red-700 dark:border-red-800 dark:text-red-300",
        contornoHover: "hover:bg-red-50 dark:hover:bg-red-500/10",
        fantasmaHover: "text-red-700 hover:bg-red-50 dark:text-red-300 dark:hover:bg-red-500/10",
        texto: "text-red-600 dark:text-red-400",
        ponto: "bg-red-500",
        aviso: "border-red-200 bg-red-50/70 text-red-900 dark:border-red-500/20 dark:bg-red-500/5 dark:text-red-200",
        anel: "focus-visible:ring-red-500/40",
        aro: "ring-red-600/20 dark:ring-red-400/30",
    },
    pink: {
        cheio: "bg-pink-600 text-white",
        cheioHover: "hover:bg-pink-700",
        suave: "bg-pink-50 text-pink-700 dark:bg-pink-500/10 dark:text-pink-300",
        suaveHover: "hover:bg-pink-100 dark:hover:bg-pink-500/20",
        contorno: "border border-pink-300 text-pink-700 dark:border-pink-800 dark:text-pink-300",
        contornoHover: "hover:bg-pink-50 dark:hover:bg-pink-500/10",
        fantasmaHover: "text-pink-700 hover:bg-pink-50 dark:text-pink-300 dark:hover:bg-pink-500/10",
        texto: "text-pink-600 dark:text-pink-400",
        ponto: "bg-pink-500",
        aviso: "border-pink-200 bg-pink-50/70 text-pink-900 dark:border-pink-500/20 dark:bg-pink-500/5 dark:text-pink-200",
        anel: "focus-visible:ring-pink-500/40",
        aro: "ring-pink-600/20 dark:ring-pink-400/30",
    },
    grape: {
        cheio: "bg-fuchsia-600 text-white",
        cheioHover: "hover:bg-fuchsia-700",
        suave: "bg-fuchsia-50 text-fuchsia-700 dark:bg-fuchsia-500/10 dark:text-fuchsia-300",
        suaveHover: "hover:bg-fuchsia-100 dark:hover:bg-fuchsia-500/20",
        contorno: "border border-fuchsia-300 text-fuchsia-700 dark:border-fuchsia-800 dark:text-fuchsia-300",
        contornoHover: "hover:bg-fuchsia-50 dark:hover:bg-fuchsia-500/10",
        fantasmaHover: "text-fuchsia-700 hover:bg-fuchsia-50 dark:text-fuchsia-300 dark:hover:bg-fuchsia-500/10",
        texto: "text-fuchsia-600 dark:text-fuchsia-400",
        ponto: "bg-fuchsia-500",
        aviso: "border-fuchsia-200 bg-fuchsia-50/70 text-fuchsia-900 dark:border-fuchsia-500/20 dark:bg-fuchsia-500/5 dark:text-fuchsia-200",
        anel: "focus-visible:ring-fuchsia-500/40",
        aro: "ring-fuchsia-600/20 dark:ring-fuchsia-400/30",
    },
    violet: {
        cheio: "bg-violet-600 text-white",
        cheioHover: "hover:bg-violet-700",
        suave: "bg-violet-50 text-violet-700 dark:bg-violet-500/10 dark:text-violet-300",
        suaveHover: "hover:bg-violet-100 dark:hover:bg-violet-500/20",
        contorno: "border border-violet-300 text-violet-700 dark:border-violet-800 dark:text-violet-300",
        contornoHover: "hover:bg-violet-50 dark:hover:bg-violet-500/10",
        fantasmaHover: "text-violet-700 hover:bg-violet-50 dark:text-violet-300 dark:hover:bg-violet-500/10",
        texto: "text-violet-600 dark:text-violet-400",
        ponto: "bg-violet-500",
        aviso: "border-violet-200 bg-violet-50/70 text-violet-900 dark:border-violet-500/20 dark:bg-violet-500/5 dark:text-violet-200",
        anel: "focus-visible:ring-violet-500/40",
        aro: "ring-violet-600/20 dark:ring-violet-400/30",
    },
    indigo: {
        cheio: "bg-indigo-600 text-white",
        cheioHover: "hover:bg-indigo-700",
        suave: "bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300",
        suaveHover: "hover:bg-indigo-100 dark:hover:bg-indigo-500/20",
        contorno: "border border-indigo-300 text-indigo-700 dark:border-indigo-800 dark:text-indigo-300",
        contornoHover: "hover:bg-indigo-50 dark:hover:bg-indigo-500/10",
        fantasmaHover: "text-indigo-700 hover:bg-indigo-50 dark:text-indigo-300 dark:hover:bg-indigo-500/10",
        texto: "text-indigo-600 dark:text-indigo-400",
        ponto: "bg-indigo-500",
        aviso: "border-indigo-200 bg-indigo-50/70 text-indigo-900 dark:border-indigo-500/20 dark:bg-indigo-500/5 dark:text-indigo-200",
        anel: "focus-visible:ring-indigo-500/40",
        aro: "ring-indigo-600/20 dark:ring-indigo-400/30",
    },
    blue: {
        cheio: "bg-blue-600 text-white",
        cheioHover: "hover:bg-blue-700 active:bg-blue-800",
        suave: "bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-300",
        suaveHover: "hover:bg-blue-100 dark:hover:bg-blue-500/20",
        contorno: "border border-blue-300 text-blue-700 dark:border-blue-800 dark:text-blue-300",
        contornoHover: "hover:bg-blue-50 dark:hover:bg-blue-500/10",
        fantasmaHover: "text-blue-700 hover:bg-blue-50 dark:text-blue-300 dark:hover:bg-blue-500/10",
        texto: "text-blue-600 dark:text-blue-400",
        ponto: "bg-blue-500",
        aviso: "border-blue-200 bg-blue-50/70 text-blue-900 dark:border-blue-500/20 dark:bg-blue-500/5 dark:text-blue-200",
        anel: "focus-visible:ring-blue-500/40",
        aro: "ring-blue-600/20 dark:ring-blue-400/30",
    },
    cyan: {
        cheio: "bg-cyan-600 text-white",
        cheioHover: "hover:bg-cyan-700",
        suave: "bg-cyan-50 text-cyan-700 dark:bg-cyan-500/10 dark:text-cyan-300",
        suaveHover: "hover:bg-cyan-100 dark:hover:bg-cyan-500/20",
        contorno: "border border-cyan-300 text-cyan-700 dark:border-cyan-800 dark:text-cyan-300",
        contornoHover: "hover:bg-cyan-50 dark:hover:bg-cyan-500/10",
        fantasmaHover: "text-cyan-700 hover:bg-cyan-50 dark:text-cyan-300 dark:hover:bg-cyan-500/10",
        texto: "text-cyan-600 dark:text-cyan-400",
        ponto: "bg-cyan-500",
        aviso: "border-cyan-200 bg-cyan-50/70 text-cyan-900 dark:border-cyan-500/20 dark:bg-cyan-500/5 dark:text-cyan-200",
        anel: "focus-visible:ring-cyan-500/40",
        aro: "ring-cyan-600/20 dark:ring-cyan-400/30",
    },
    teal: {
        cheio: "bg-teal-600 text-white",
        cheioHover: "hover:bg-teal-700 active:bg-teal-800",
        suave: "bg-teal-50 text-teal-700 dark:bg-teal-500/10 dark:text-teal-300",
        suaveHover: "hover:bg-teal-100 dark:hover:bg-teal-500/20",
        contorno: "border border-teal-300 text-teal-700 dark:border-teal-800 dark:text-teal-300",
        contornoHover: "hover:bg-teal-50 dark:hover:bg-teal-500/10",
        fantasmaHover: "text-teal-700 hover:bg-teal-50 dark:text-teal-300 dark:hover:bg-teal-500/10",
        texto: "text-teal-600 dark:text-teal-400",
        ponto: "bg-teal-500",
        aviso: "border-teal-200 bg-teal-50/70 text-teal-900 dark:border-teal-500/20 dark:bg-teal-500/5 dark:text-teal-200",
        anel: "focus-visible:ring-teal-500/40",
        aro: "ring-teal-600/20 dark:ring-teal-400/30",
    },
    green: {
        cheio: "bg-green-600 text-white",
        cheioHover: "hover:bg-green-700 active:bg-green-800",
        suave: "bg-green-50 text-green-700 dark:bg-green-500/10 dark:text-green-300",
        suaveHover: "hover:bg-green-100 dark:hover:bg-green-500/20",
        contorno: "border border-green-300 text-green-700 dark:border-green-800 dark:text-green-300",
        contornoHover: "hover:bg-green-50 dark:hover:bg-green-500/10",
        fantasmaHover: "text-green-700 hover:bg-green-50 dark:text-green-300 dark:hover:bg-green-500/10",
        texto: "text-green-600 dark:text-green-400",
        ponto: "bg-green-500",
        aviso: "border-green-200 bg-green-50/70 text-green-900 dark:border-green-500/20 dark:bg-green-500/5 dark:text-green-200",
        anel: "focus-visible:ring-green-500/40",
        aro: "ring-green-600/20 dark:ring-green-400/30",
    },
    lime: {
        cheio: "bg-lime-500 text-lime-950",
        cheioHover: "hover:bg-lime-600",
        suave: "bg-lime-50 text-lime-800 dark:bg-lime-500/10 dark:text-lime-300",
        suaveHover: "hover:bg-lime-100 dark:hover:bg-lime-500/20",
        contorno: "border border-lime-400 text-lime-800 dark:border-lime-800 dark:text-lime-300",
        contornoHover: "hover:bg-lime-50 dark:hover:bg-lime-500/10",
        fantasmaHover: "text-lime-800 hover:bg-lime-50 dark:text-lime-300 dark:hover:bg-lime-500/10",
        texto: "text-lime-700 dark:text-lime-400",
        ponto: "bg-lime-500",
        aviso: "border-lime-200 bg-lime-50/70 text-lime-900 dark:border-lime-500/20 dark:bg-lime-500/5 dark:text-lime-200",
        anel: "focus-visible:ring-lime-500/40",
        aro: "ring-lime-600/30 dark:ring-lime-400/30",
    },
    yellow: {
        cheio: "bg-yellow-400 text-yellow-950",
        cheioHover: "hover:bg-yellow-500",
        suave: "bg-yellow-50 text-yellow-800 dark:bg-yellow-500/10 dark:text-yellow-300",
        suaveHover: "hover:bg-yellow-100 dark:hover:bg-yellow-500/20",
        contorno: "border border-yellow-400 text-yellow-800 dark:border-yellow-800 dark:text-yellow-300",
        contornoHover: "hover:bg-yellow-50 dark:hover:bg-yellow-500/10",
        fantasmaHover: "text-yellow-800 hover:bg-yellow-50 dark:text-yellow-300 dark:hover:bg-yellow-500/10",
        texto: "text-yellow-700 dark:text-yellow-400",
        ponto: "bg-yellow-400",
        aviso: "border-yellow-200 bg-yellow-50/70 text-yellow-900 dark:border-yellow-500/20 dark:bg-yellow-500/5 dark:text-yellow-200",
        anel: "focus-visible:ring-yellow-500/40",
        aro: "ring-yellow-600/30 dark:ring-yellow-400/30",
    },
    orange: {
        cheio: "bg-orange-500 text-white",
        cheioHover: "hover:bg-orange-600 active:bg-orange-700",
        suave: "bg-orange-50 text-orange-700 dark:bg-orange-500/10 dark:text-orange-300",
        suaveHover: "hover:bg-orange-100 dark:hover:bg-orange-500/20",
        contorno: "border border-orange-300 text-orange-700 dark:border-orange-800 dark:text-orange-300",
        contornoHover: "hover:bg-orange-50 dark:hover:bg-orange-500/10",
        fantasmaHover: "text-orange-700 hover:bg-orange-50 dark:text-orange-300 dark:hover:bg-orange-500/10",
        texto: "text-orange-600 dark:text-orange-400",
        ponto: "bg-orange-500",
        aviso: "border-orange-200 bg-orange-50/70 text-orange-900 dark:border-orange-500/20 dark:bg-orange-500/5 dark:text-orange-200",
        anel: "focus-visible:ring-orange-500/40",
        aro: "ring-orange-600/20 dark:ring-orange-400/30",
    },
};

/** Sinónimos que também aparecem nos dados (`success`, `danger`, `amarela`…). */
const SINONIMOS: Record<string, Cor> = {
    primary: "marca",
    brand: "marca",
    emerald: "green",
    success: "green",
    verde: "green",
    danger: "red",
    error: "red",
    vermelho: "red",
    vermelha: "red",
    warning: "orange",
    laranja: "orange",
    amarelo: "yellow",
    amarela: "yellow",
    azul: "blue",
    info: "blue",
    roxo: "violet",
    purple: "violet",
    cinza: "gray",
    grey: "gray",
    secondary: "gray",
};

/** A paleta de uma cor. Uma cor desconhecida (ou vinda vazia do servidor) dá o cinzento. */
export function paleta(cor?: string | null): Paleta {
    if (!cor) {
        return PALETAS.gray;
    }
    const chave = cor.toLowerCase() as Cor;
    return PALETAS[chave] ?? PALETAS[SINONIMOS[chave] ?? "gray"];
}

/** A cor normalizada (`success` → `green`, desconhecida → `gray`). */
export function corValida(cor?: string | null): Cor {
    if (!cor) {
        return "gray";
    }
    const chave = cor.toLowerCase();
    return (chave in PALETAS ? chave : SINONIMOS[chave] ?? "gray") as Cor;
}

/** Os tons de cada cor que um portal pode ter (600, 700, 50, 700 e 400 do Tailwind; o âmbar vai um tom acima). */
const TONS_DO_PORTAL: Record<string, [string, string, string, string, string]> = {
    marca: ["#06954d", "#047857", "#ecfdf5", "#047857", "#34d399"],
    green: ["#16a34a", "#15803d", "#f0fdf4", "#15803d", "#4ade80"],
    teal: ["#0d9488", "#0f766e", "#f0fdfa", "#0f766e", "#2dd4bf"],
    blue: ["#2563eb", "#1d4ed8", "#eff6ff", "#1d4ed8", "#60a5fa"],
    indigo: ["#4f46e5", "#4338ca", "#eef2ff", "#4338ca", "#818cf8"],
    violet: ["#7c3aed", "#6d28d9", "#f5f3ff", "#6d28d9", "#a78bfa"],
    // O LogiTransporte: o âmbar do logótipo. O tom principal é o 700 (e não o 600) porque o
    // texto branco em cima do 600 não chega ao contraste mínimo.
    amber: ["#b45309", "#92400e", "#fffbeb", "#92400e", "#fbbf24"],
    // A Escola Inteligente: o azul-escuro da marca (#083657), igual em todas as escolas.
    escola: ["#083657", "#062a45", "#eef4f9", "#083657", "#7db4e3"],
    // O azul do Logigate, o mesmo do logótipo (era o `logigate` do tema Mantine: 6, 7, 0, 7 e 3).
    logigate: ["#1A56DB", "#1648B8", "#E7F0FF", "#1648B8", "#6BA0F5"],
    // O FacturaYetu: o índigo do ícone da app (#1709D5), com o 300 da escala para o escuro.
    facturayetu: ["#1709d5", "#1207ae", "#eeedfe", "#1207ae", "#9187f8"],
};

export type CorDoPortal = "marca" | "green" | "teal" | "blue" | "indigo" | "violet" | "amber" | "escola" | "logigate" | "facturayetu";

/**
 * Escolhe a cor da «marca» deste portal: tudo o que no kit é `marca` (o botão principal, o
 * separador aceso, o foco dos campos, o menu lateral) passa a sair nesta cor. Chama-se uma
 * vez no `app.tsx`, antes de montar a aplicação.
 */
export function definirCorDoPortal(cor: CorDoPortal): void {
    if (typeof document === "undefined") {
        return;
    }
    const [portal, forte, suave, texto, claro] = TONS_DO_PORTAL[cor] ?? TONS_DO_PORTAL.marca;
    const raiz = document.documentElement.style;
    raiz.setProperty("--cor-portal", portal);
    raiz.setProperty("--cor-portal-forte", forte);
    raiz.setProperty("--cor-portal-suave", suave);
    raiz.setProperty("--cor-portal-texto", texto);
    raiz.setProperty("--cor-portal-claro", claro);
}
